import { isSameDay, isAfter, isBefore } from 'date-fns';
import * as React from 'react';
import { useState } from 'react';
import DatePicker from 'react-datepicker';

import { Button } from './buttons/Button';
import { Close } from '../assets/Close';
import { Pencil } from '../assets/Pencil';
import { InlineSpinner } from '../assets/Spinner';
import { Fulfillment, Shipment } from '../pages/types';
import { formatDateTime } from '../utils';

interface ShipmentTimestamps {
  shipped: Date | null;
  in_transit: Date | null;
  delivered: Date | null;
}

export type TimestampsMap = Record<string, ShipmentTimestamps>;

interface MarkCompleteModalProps {
  order: any;
  onConfirm: (timestamps: TimestampsMap) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

const PencilButton = React.forwardRef<HTMLButtonElement, { onClick?: React.MouseEventHandler }>(
  ({ onClick }, ref) => (
    <button ref={ref} type="button" onClick={onClick} className="ml-2 shrink-0 cursor-pointer">
      <Pencil className="w-4 h-4" />
    </button>
  )
);

const buildInitialTimestamps = (shipments: Shipment[]): TimestampsMap =>
  Object.fromEntries(
    shipments.map((s) => [
      s.id,
      {
        shipped: s.shipped ? new Date(s.shipped) : null,
        in_transit: s.in_transit ? new Date(s.in_transit) : null,
        delivered: s.delivered ? new Date(s.delivered) : null,
      },
    ])
  );

export const MarkCompleteModal = ({
  order,
  onConfirm,
  onCancel,
  isLoading = false,
}: MarkCompleteModalProps) => {
  const shipments: Shipment[] = (order.fulfillments ?? []).flatMap(
    (f: Fulfillment) => f.shipments ?? []
  );

  const [timestamps, setTimestamps] = useState<TimestampsMap>(() =>
    buildInitialTimestamps(shipments)
  );

  const now = new Date();

  const setField = (shipmentId: string, field: keyof ShipmentTimestamps, value: Date | null) => {
    setTimestamps((prev) => ({
      ...prev,
      [shipmentId]: { ...prev[shipmentId], [field]: value },
    }));
  };

  const allComplete = shipments.every((s) => {
    const ts = timestamps[s.id];
    return ts?.shipped && ts?.in_transit && ts?.delivered;
  });

  const filterPastTime = (time: Date) => time <= now;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-md p-6 w-[560px] max-h-[80vh] overflow-y-auto relative border">
        <Close className="absolute top-2 right-2 w-8 h-8 cursor-pointer" onClick={onCancel} />
        <h3 className="text-lg font-semibold mb-3 text-center mt-2">Mark order as complete?</h3>
        <p className="text-sm mb-5 text-center">
          In order to mark this order as complete, please{' '}
          <strong>provide the shipped, in-transit, and delivered timestamps</strong> for all
          shipments on this order.
        </p>

        <div className="space-y-3">
          {shipments.map((shipment) => {
            const ts = timestamps[shipment.id];
            const shipped = ts?.shipped ?? null;
            const inTransit = ts?.in_transit ?? null;
            const delivered = ts?.delivered ?? null;

            const inTransitMinDate = shipped ?? undefined;
            const deliveredMinDate = inTransit ?? shipped ?? undefined;

            const filterShippedTime = (time: Date) => {
              if (!filterPastTime(time)) return false;
              const successor = inTransit ?? delivered;
              if (successor && isSameDay(time, successor)) return isBefore(time, successor);
              return true;
            };

            const filterInTransitTime = (time: Date) => {
              if (!filterPastTime(time)) return false;
              if (shipped && isSameDay(time, shipped)) return isAfter(time, shipped);
              if (delivered && isSameDay(time, delivered)) return isBefore(time, delivered);
              return true;
            };

            const filterDeliveredTime = (time: Date) => {
              if (!filterPastTime(time)) return false;
              const predecessor = inTransit ?? shipped;
              if (predecessor && isSameDay(time, predecessor)) return isAfter(time, predecessor);
              return true;
            };

            const tracking = shipment.tracking_number;

            const pickerBase = {
              popperProps: { strategy: 'fixed' as const },
              popperPlacement: 'bottom-end' as const,
              popperClassName: 'z-[9999]',
              showTimeSelect: true,
              timeFormat: 'h:mm aa',
              timeIntervals: 15,
              dateFormat: 'M/d/yyyy',
              maxDate: now,
            };

            return (
              <div key={shipment.id} className="bg-blue-50 rounded-lg px-3 py-2">
                <div className="flex items-center text-sm mb-1">
                  {tracking && (
                    <span className="font-semibold underline mr-2 shrink-0 text-sm">
                      {tracking}:
                    </span>
                  )}
                  <span className={`flex-1 ${shipped ? '' : 'font-bold'}`}>
                    {shipped
                      ? `Shipped ${formatDateTime(shipped.toISOString(), true)}`
                      : 'Provide shipped timestamp'}
                  </span>
                  <DatePicker
                    {...pickerBase}
                    key={`${shipment.id}-shipped-${inTransit?.getTime()}-${delivered?.getTime()}`}
                    selected={shipped}
                    maxDate={inTransit ?? delivered ?? now}
                    filterTime={filterShippedTime}
                    onChange={(date) => setField(shipment.id, 'shipped', date)}
                    customInput={<PencilButton />}
                  />
                </div>
                <div className="flex items-center text-sm mb-1">
                  {tracking && (
                    <span
                      className="font-semibold opacity-0 pointer-events-none select-none mr-2 shrink-0 text-sm"
                      aria-hidden="true"
                    >
                      {tracking}:
                    </span>
                  )}
                  <span className={`flex-1 ${inTransit ? '' : 'font-bold'}`}>
                    {inTransit
                      ? `In-transit ${formatDateTime(inTransit.toISOString(), true)}`
                      : 'Provide in-transit timestamp'}
                  </span>
                  <DatePicker
                    {...pickerBase}
                    key={`${shipment.id}-in-transit-${shipped?.getTime()}-${delivered?.getTime()}`}
                    selected={inTransit}
                    minDate={inTransitMinDate}
                    maxDate={delivered ?? now}
                    filterTime={filterInTransitTime}
                    onChange={(date) => setField(shipment.id, 'in_transit', date)}
                    customInput={<PencilButton />}
                  />
                </div>
                <div className="flex items-center text-sm">
                  {tracking && (
                    <span
                      className="font-semibold opacity-0 pointer-events-none select-none mr-2 shrink-0 text-sm"
                      aria-hidden="true"
                    >
                      {tracking}:
                    </span>
                  )}
                  <span className={`flex-1 ${delivered ? '' : 'font-bold'}`}>
                    {delivered
                      ? `Delivered ${formatDateTime(delivered.toISOString(), true)}`
                      : 'Provide delivery timestamp'}
                  </span>
                  <DatePicker
                    {...pickerBase}
                    key={`${shipment.id}-delivered-${(inTransit ?? shipped)?.getTime()}`}
                    selected={delivered}
                    minDate={deliveredMinDate}
                    filterTime={filterDeliveredTime}
                    onChange={(date) => setField(shipment.id, 'delivered', date)}
                    customInput={<PencilButton />}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex justify-center mt-5">
          <Button
            variant="primary"
            label={isLoading ? <InlineSpinner /> : 'Confirm'}
            isDisabled={!allComplete || isLoading}
            onClick={() => onConfirm(timestamps)}
          />
        </div>
      </div>
    </div>
  );
};
